import { ErrorWithBreadcrumb } from '~/errors';

export const useAccountSettingsStore = defineStore('accountSettings', () => {
  const cacheProviderAPI = useCacheProviderAPI();

  // Using a Record to ensure that we have all the keys of AccountSettings in the object and no duplicates. The values dont matter, so we use null.
  const accountSettingsToFetch: Record<keyof AccountSettings, null> = {
    StatusChangeToCompletedRequiresNote: null,
    StatusChangeToPartiallyCompletedRequiresNote: null,
    CreateCalendarEventAccessLevel: null,
    ReadCalendarEventAccessLevel: null,
    LockedEventsCanBeMoved: null,
    LockedEventsCanBeUnlocked: null,
    PlanningDuration: null,
    UseInventory: null,
    ShowMaterialFavoriteList: null,
    MaterialPricesVisibilityInApp: null,
    LocationRequiredOnMaterialPage: null,
    MainLocationID: null,
    ShowOfferInApp: null,
    ShowNoQaButtonInApp: null,
    DebtorsFinancialAccountID: null,
    DefaultDebtorPaymentTermId: null,
    UseHourSpecificationAsDefault: null,
    IncludeEndDateForRentalCount: null,
    AllowMaterialMovementBelowZero: null,
    UseQACopySection: null,
    DefaultImageAppSize: null,
    AllowFitterAddQAToProjectInApp: null,
    PlanningWorkDayStartTime: null,
    PlanningWorkDayEndTime: null,
    NoteRequiredForHourRegistration: null,
    StartAndEndTimeRequiredForHourRegistration: null,
    WorksheetRequiresMaterialItems: null,
    WorksheetRequiresTimeRegistration: null,
    FitterAllowedToEditProjectDescription: null,
    FitterAllowedToEditWorksheetDescription: null,
    AllowEditProjectDescription: null,
    AllowEditWorkDescription: null,
    UseCostCodes: null,
    AllowCreateQALocation: null,
    ShowDebtorNoteOnWorksheet: null,
    DepartmentOnProjectRequired: null,
    CarrierOnProjectRequired: null,
    PurposeOnProjectRequired: null,
    IsDimensionDepartmentDisplayedOnApp: null,
    IsDimensionCarrierDisplayedOnApp: null,
    IsDimensionPurposeDisplayedOnApp: null,
    DefaultDepartmentOnProjectForApp: null,
    DefaultCarrierOnProjectForApp: null,
    DefaultPurposeOnProjectForApp: null,
    ShowSellerInApp: null,
    DefaultMobileProjectGroupID: null,
    FugeQA: null,
    ShowBaseSalaryInHourOverview: null,
    ShowMiscellaneousSalaryInHourOverview: null,
    ShowOvertimeSalaryInHourOverview: null,
    CanEditProjectNotesFromApp: null,
    CanDeleteProjectNotesFromApp: null,
    AllowLettersInProjectReference: null,
    UseBackgroundCheckViaPSecure: null,
    ShowBasketInApp: null,
    BrowserTitle: null
  };

  // The Type assertions are necessary here if we want typed accountSettings. I doubt there is a non-crazy way to avoid it.
  async function getAccountSettings() {
    const response = await cacheProviderAPI.fetchAccountSettings(Object.keys(accountSettingsToFetch) as (keyof AccountSettings)[]);

    if (response && response.HasErrors === false) {
      const settings: Partial<Record<keyof AccountSettings, unknown>> = {};

      response.Data.forEach((setting) => {
        settings[setting.Name] = setting.Value;
      });

      _accountSettings.value = settings as AccountSettings;
    }
  }

  const _accountSettings = ref<AccountSettings | null>(null);

  const accountSettings = computed<AccountSettings>(() => {
    if (_accountSettings.value === null) {
      throw new ErrorWithBreadcrumb({ category: BreadCrumbCategoryEnum.AccountSettings, message: 'Account settings are not loaded yet' });
    }

    return readonly(_accountSettings.value);
  });

  return {
    _accountSettings,
    accountSettings,
    getAccountSettings
  };
});
